import React, { useState, useEffect } from "react";
import { Table, Button, Icon, Popup } from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import { LoadingSpinner, BasicModal, ConfirmDialog } from "../../../Shared";
import { Record } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { RecordForm } from "../RecordForm";
import searchSvg from "../../../../assets/svg/search.svg";
import "./ListRecords.scss";

const recordController = new Record();

export function ListRecords({ active: activeFilter, reload, onReload }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [items,       setItems]       = useState(null);
  const [itemsFilter, setItemsFilter] = useState([]);
  const [search,      setSearch]      = useState("");
  const [showModal,   setShowModal]   = useState(false);
  const [selected,    setSelected]    = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmMsg,  setConfirmMsg]  = useState("");
  const [isDelete,    setIsDelete]    = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setItems(null);
        const res = await recordController.getRecords(accessToken, activeFilter);
        setItems(res);
        setItemsFilter(res);
      } catch {
        setItems([]);
        setItemsFilter([]);
      }
    })();
  }, [activeFilter, reload]);

  useEffect(() => {
    if (!items) return;
    if (!search) { setItemsFilter(items); return; }
    const q = search.toLowerCase();
    setItemsFilter(
      items.filter((i) =>
        i.numero?.toLowerCase().includes(q) ||
        i.nombre?.toLowerCase().includes(q)
      )
    );
  }, [search, items]);

  const openCreate = () => { setSelected(null); setShowModal(true); };
  const openEdit   = (i) => { setSelected(i);   setShowModal(true); };

  const openToggle = (i) => {
    setSelected(i); setIsDelete(false);
    setConfirmMsg(`¿${i.active ? t("deactivate") : t("activate")} "${i.numero}"?`);
    setShowConfirm(true);
  };

  const openDelete = (i) => {
    setSelected(i); setIsDelete(true);
    setConfirmMsg(`¿${t("delete")} "${i.numero}"?`);
    setShowConfirm(true);
  };

  const handleToggle = async () => {
    try {
      await recordController.updateRecord(accessToken, selected._id, { active: !selected.active });
      toast.success(selected.active ? t("deactivated") : t("activated"));
      setShowConfirm(false); onReload();
    } catch { toast.error(t("error")); }
  };

  const handleDelete = async () => {
    try {
      await recordController.deleteRecord(accessToken, selected._id);
      toast.success(t("deleted"));
      setShowConfirm(false); onReload();
    } catch (err) { toast.error(err?.msg || t("error")); }
  };

  if (items === null) return <LoadingSpinner />;

  return (
    <div className="records-list">
      <ToastContainer position="top-right" autoClose={3000} />

      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem" }}>
        <div className="search-user-wrapper">
          <img src={searchSvg} alt="search" className="search-user-icon" />
          <input
            className="search-user-input"
            placeholder={`${t("search")}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Button icon className="add-records-btn" onClick={openCreate}>
          <Icon name="plus" />
        </Button>
      </div>

      {itemsFilter.length === 0 ? (
        <p style={{ color: "#999" }}>{t("no_results")}</p>
      ) : (
        <Table className="records-table" selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>#</Table.HeaderCell>
              <Table.HeaderCell>{t("record_number")}</Table.HeaderCell>
              <Table.HeaderCell>{t("name")}</Table.HeaderCell>
              <Table.HeaderCell>{t("surname")}</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">{t("actions")}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {itemsFilter.map((i, idx) => (
              <Table.Row key={i._id}>
                <Table.Cell>{idx + 1}</Table.Cell>
                <Table.Cell>{i.numero}</Table.Cell>
                <Table.Cell>{i.nombre  || "-"}</Table.Cell>
                <Table.Cell>{i.apellido || "-"}</Table.Cell>
                <Table.Cell textAlign="center">
                  <Popup content={t("edit")} trigger={
                    <Button icon primary size="mini" onClick={() => openEdit(i)}>
                      <Icon name="pencil" />
                    </Button>
                  } />
                  <Popup content={i.active ? t("deactivate") : t("activate")} trigger={
                    <Button icon size="mini" color={i.active ? "orange" : "teal"} onClick={() => openToggle(i)}>
                      <Icon name={i.active ? "ban" : "check"} />
                    </Button>
                  } />
                  <Popup content={t("delete")} trigger={
                    <Button icon size="mini" color="red" onClick={() => openDelete(i)}>
                      <Icon name="trash" />
                    </Button>
                  } />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      <BasicModal
        show={showModal}
        close={() => setShowModal(false)}
        title={selected ? `${t("edit")}: ${selected.numero}` : t("new_record")}
        size="small"
      >
        <RecordForm record={selected} close={() => setShowModal(false)} onReload={onReload} />
      </BasicModal>

      <ConfirmDialog
        open={showConfirm}
        title={isDelete ? "Eliminar" : (selected?.active ? "Desactivar" : "Activar")}
        message={confirmMsg}
        onCancel={() => setShowConfirm(false)}
        onConfirm={isDelete ? handleDelete : handleToggle}
        confirmLabel="Aceptar"
        cancelLabel={t("cancel")}
        confirmDanger={isDelete}
      />
    </div>
  );
}
